<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';
import aiAgentAvatar from 'dashboard/assets/images/ai_agent_avatar.svg';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  isSystemAiAgent: {
    type: Boolean,
    default: false,
  },
  thumbnail: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);
const agentCredentials = ref({ email: props.email });
const avatarFile = ref(null);
const avatarUrl = ref(props.thumbnail);
const removeAvatar = ref(false);
const avatarInput = ref(null);

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: props.isSystemAiAgent ? {} : { required },
  agentAvailability: props.isSystemAiAgent ? {} : { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(() =>
  props.isSystemAiAgent
    ? t('AGENT_MGMT.EDIT.AI_TITLE')
    : `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const displayedAvatarUrl = computed(() => avatarUrl.value || aiAgentAvatar);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = props.isSystemAiAgent
      ? {
          id: props.id,
          isFormData: true,
          ai_display_name: agentName.value,
          ai_avatar: avatarFile.value,
          remove_ai_avatar: removeAvatar.value,
        }
      : {
          id: props.id,
          name: agentName.value,
          availability: agentAvailability.value,
        };

    if (
      !props.isSystemAiAgent &&
      selectedRole.value.name.startsWith('custom_')
    ) {
      payload.custom_role_id = selectedRole.value.id;
    } else if (!props.isSystemAiAgent) {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const handleAvatarSelect = event => {
  const [file] = event.target.files;
  if (!file) return;

  avatarFile.value = file;
  avatarUrl.value = URL.createObjectURL(file);
  removeAvatar.value = false;
};

const chooseAvatar = () => avatarInput.value?.click();

const resetAvatar = () => {
  avatarFile.value = null;
  avatarUrl.value = '';
  if (avatarInput.value) avatarInput.value.value = null;
  removeAvatar.value = true;
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div v-if="isSystemAiAgent" class="flex flex-col gap-5 px-6 pb-6">
        <section class="flex flex-col gap-3">
          <div class="flex items-center justify-between gap-4">
            <div>
              <h3 class="mb-1 text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_MGMT.EDIT.AI_AVATAR_LABEL') }}
              </h3>
              <p class="mb-0 text-xs text-n-slate-11">
                {{ $t('AGENT_MGMT.EDIT.AI_AVATAR_HELPER') }}
              </p>
            </div>
            <span
              v-if="!avatarUrl"
              class="flex-shrink-0 px-2 py-1 text-xs font-medium rounded-md bg-n-brand/10 text-n-blue-text"
            >
              {{ $t('AGENT_MGMT.EDIT.AI_DEFAULT_AVATAR') }}
            </span>
          </div>

          <div
            class="flex flex-col gap-4 p-4 border rounded-xl border-n-weak sm:flex-row sm:items-center sm:justify-between"
          >
            <div class="flex items-center gap-3">
              <div
                class="flex items-center justify-center flex-shrink-0 overflow-hidden border rounded-xl size-16 border-n-weak bg-n-solid-2"
              >
                <img
                  :src="displayedAvatarUrl"
                  :alt="$t('AGENT_MGMT.EDIT.AI_AVATAR_LABEL')"
                  class="object-cover w-full h-full"
                />
              </div>
              <div>
                <p class="mb-1 text-sm font-medium text-n-slate-12">
                  {{ $t('AGENT_MGMT.EDIT.AI_AVATAR_LABEL') }}
                </p>
                <p class="mb-0 text-xs text-n-slate-11">
                  {{
                    avatarUrl
                      ? $t('AGENT_MGMT.EDIT.AI_CUSTOM_AVATAR')
                      : $t('AGENT_MGMT.EDIT.AI_DEFAULT_AVATAR')
                  }}
                </p>
              </div>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <input
                ref="avatarInput"
                type="file"
                accept="image/png, image/jpeg, image/jpg, image/gif"
                class="hidden"
                @change="handleAvatarSelect"
              />
              <Button
                outline
                slate
                type="button"
                icon="i-lucide-upload"
                :label="$t('AGENT_MGMT.EDIT.AI_CHANGE_AVATAR')"
                @click="chooseAvatar"
              />
              <Button
                v-if="avatarUrl"
                ghost
                ruby
                type="button"
                icon="i-lucide-rotate-ccw"
                :label="$t('AGENT_MGMT.EDIT.AI_RESET_AVATAR')"
                @click="resetAvatar"
              />
            </div>
          </div>
        </section>

        <section class="flex flex-col gap-1">
          <Input
            v-model="agentName"
            :label="$t('AGENT_MGMT.EDIT.FORM.NAME.LABEL')"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            :message="
              v$.agentName.$error
                ? $t('AGENT_MGMT.EDIT.FORM.NAME.ERROR')
                : $t('AGENT_MGMT.EDIT.AI_NAME_HELPER')
            "
            :message-type="v$.agentName.$error ? 'error' : 'info'"
            @input="v$.agentName.$touch"
            @blur="v$.agentName.$touch"
          />
        </section>
      </div>

      <div v-else class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div v-if="!isSystemAiAgent" class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div v-if="!isSystemAiAgent" class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <div
        class="flex flex-row w-full gap-2"
        :class="
          isSystemAiAgent
            ? 'justify-end px-6 py-4 border-t border-n-weak'
            : 'justify-start px-0 py-2'
        "
      >
        <div
          v-if="!isSystemAiAgent"
          class="w-[50%] ltr:text-left rtl:text-right"
        >
          <Button
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div
          class="flex justify-end items-center gap-2"
          :class="isSystemAiAgent ? '' : 'w-[50%]'"
        >
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
